import React from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Play, Download, Music, BookOpen, Mic, Palette, Building2, ChevronRight } from 'lucide-react';

const CorporateHomePage = () => {
  const navigate = useNavigate();

  const divisions = [
    {
      id: 'mindset-shift-media',
      name: 'Mindset Shift Media',
      description: 'Transformational media for individuals and organizations.',
      icon: Music,
      path: '/divisions/mindset-shift-media',
      color: 'from-purple-600 to-pink-600'
    },
    {
      id: 'author-growth-systems',
      name: 'Author Growth Systems',
      subtitle: 'Featuring the Ultimate Author Social Media eCoSystem',
      description: 'Marketing infrastructure for modern authors.',
      icon: BookOpen,
      path: '/divisions/author-growth-systems',
      color: 'from-green-600 to-emerald-600'
    },
    {
      id: 'publishing-ghostwriting',
      name: 'Publishing & Ghostwriting',
      description: 'Manuscript development and authority positioning.',
      icon: Mic,
      path: '/divisions/publishing-ghostwriting',
      color: 'from-blue-600 to-cyan-600'
    },
    {
      id: 'brand-infrastructure',
      name: 'Brand Infrastructure',
      description: 'Strategic marketing, design, and visual identity systems.',
      icon: Palette,
      path: '/divisions/brand-infrastructure',
      color: 'from-orange-600 to-amber-600'
    },
    {
      id: 'business-directory',
      name: 'Business Directory',
      description: 'Visibility and business credibility ecosystem.',
      icon: Building2,
      path: '/divisions/business-directory',
      color: 'from-red-600 to-rose-600'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* SECTION 1: HERO */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-red-900/20 via-transparent to-transparent"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center py-20">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/67pzcjdk_eBlackBookListings.png" 
              alt="eBlackBookListings LLC"
              className="h-20 lg:h-24 w-auto mx-auto mb-6"
            />
          </div>
          
          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white mb-4 tracking-tight">
            eBlackBookListings LLC
          </h1>
          <p className="text-xl sm:text-2xl lg:text-3xl text-red-500 font-semibold mb-6">
            We build media. We scale authors. We engineer growth.
          </p>
          
          {/* Subheadline */}
          <p className="text-base sm:text-lg text-gray-400 max-w-4xl mx-auto mb-12 leading-relaxed">
            eBlackBookListings operates as a multi-division digital infrastructure company serving authors, organizations, and growth-driven brands through media, publishing systems, strategic brand development, and scalable digital assets.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => document.getElementById('divisions').scrollIntoView({ behavior: 'smooth' })}
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 text-lg rounded-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-600/30"
            >
              Explore Divisions
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate('/store')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 text-lg rounded-lg transition-all duration-300"
            >
              Visit Store
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-8 h-8 text-white/50 rotate-90" />
        </div>
      </section>

      {/* SECTION 2: FEATURED REVENUE */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Featured Products</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto"></div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Mindset Shift Soundtrack */}
            <div className="bg-gradient-to-br from-purple-900/40 via-pink-900/30 to-black border border-purple-500/30 rounded-2xl p-8 hover:border-purple-500/60 transition-all duration-300 group">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="relative flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_egypt-portfolio/artifacts/ujuixs3p_MINDSET%20SHIFT%20Soundtrack%20Cover.png"
                    alt="Mindset Shift Soundtrack"
                    className="w-48 h-48 object-cover rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    NEW
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <Music className="w-5 h-5 text-purple-400" />
                    <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider">Soundtrack</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Mindset Shift</h3>
                  <p className="text-gray-400 mb-4">The Official Soundtrack - 11 transformational tracks designed to shift your mindset and elevate your journey.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                    <Button 
                      onClick={() => navigate('/mindset-shift-soundtrack')}
                      className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Purchase - $9.99
                    </Button>
                    <a
                      href="https://distrokid.com/hyperfollow/egyptyoung/mindset-shift-the-official-soundtrack-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white w-full sm:w-auto">
                        <Play className="w-4 h-4 mr-2" />
                        Stream
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Author Social Media eCoSystem */}
            <div className="bg-gradient-to-br from-green-900/40 via-emerald-900/30 to-black border border-green-500/30 rounded-2xl p-8 hover:border-green-500/60 transition-all duration-300 group">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="relative flex-shrink-0">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png"
                    alt="Author Social Media eCoSystem"
                    className="w-48 h-48 object-cover rounded-xl shadow-2xl group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                    BESTSELLER
                  </div>
                </div>
                <div className="text-center md:text-left flex-1">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <BookOpen className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 text-sm font-semibold uppercase tracking-wider">Digital Resource</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">The Ultimate Author Social Media eCoSystem</h3>
                  <p className="text-gray-400 mb-4">400+ verified platforms where millions of readers are waiting to discover YOUR book.</p>
                  <Button 
                    onClick={() => navigate('/author-ecosystem')}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: DIVISIONS PREVIEW */}
      <section id="divisions" className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">Our Divisions</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Five specialized divisions engineered to build, publish, and scale your brand.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {divisions.map((division) => {
              const Icon = division.icon;
              return (
                <div 
                  key={division.id}
                  className="group bg-gray-900/50 border border-gray-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${division.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{division.name}</h3>
                  {division.subtitle && (
                    <p className="text-sm text-gray-500 mb-2 italic">{division.subtitle}</p>
                  )}
                  <p className="text-gray-400 mb-6 leading-relaxed">{division.description}</p>
                  <Button 
                    onClick={() => navigate(division.path)}
                    variant="outline"
                    className="border-gray-700 text-gray-300 hover:border-red-600 hover:text-red-500 group-hover:border-red-600 group-hover:text-red-500 transition-all duration-300"
                  >
                    Explore Division
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4: AUTHORITY & MEDIA */}
      <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Content */}
              <div>
                <span className="text-red-500 text-sm font-semibold uppercase tracking-wider mb-4 block">Authority & Media</span>
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                  The Movement Magazine
                </h2>
                <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                  18+ years of publishing excellence. Exclusive celebrity interviews with Kevin Hart, Bruno Mars, Tyrese Gibson, Larenz Tate, Frankie Beverly, and many more industry icons.
                </p>
                <div className="grid grid-cols-3 gap-6 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500 mb-1">18+</div>
                    <div className="text-sm text-gray-500">Years</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500 mb-1">100+</div>
                    <div className="text-sm text-gray-500">Interviews</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500 mb-1">A-List</div>
                    <div className="text-sm text-gray-500">Access</div>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/portfolio')}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  View Portfolio
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              
              {/* Visual */}
              <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl p-8 lg:p-12 text-center">
                <Mic className="w-20 h-20 text-white/80 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">Celebrity Access</h3>
                <p className="text-white/80 leading-relaxed">
                  Exclusive interviews and features with industry icons, entrepreneurs, and change-makers who shape culture and inspire millions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: FINAL CTA */}
      <section className="py-24 bg-black relative overflow-hidden">
        {/* Background accent */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-red-900/30 via-transparent to-transparent"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
              Build. Publish. Scale.
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Ready to transform your ideas into impactful media? Let's build something extraordinary together.
            </p>
            <Button 
              onClick={() => navigate('/start-project')}
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-5 text-xl rounded-lg transition-all duration-300 hover:shadow-2xl hover:shadow-red-600/40 hover:scale-105"
            >
              Apply to Work With Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CorporateHomePage;
